import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../apiConfig';
import styles from './PaymentSettings.module.css';

export function useStripeOnboardingStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAccountStatus = useCallback(async () => {
    try {
      const response = await api.get('/stripe/account-status');
      setStatus(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAccountStatus();
  }, [checkAccountStatus]);

  return { status, loading, error, refetch: checkAccountStatus };
}

const PaymentSettings = () => {
  const { status, loading, error, refetch } = useStripeOnboardingStatus();

  const handleConnect = async () => {
    try {
      const response = await api.post('/stripe/connect/account-link');
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error creating account link:', err);
      alert('Error creating account link: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading payment settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h3>Error Loading Payment Settings</h3>
          <p>{error}</p>
          <button onClick={refetch} className={styles.button}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!status || status.status === 'not_connected') {
    return (
      <div className={styles.container}>
        <div className={styles.connectPrompt}>
          <h3>Connect Your Stripe Account</h3>
          <p>To receive payments for your artwork, you need to connect your Stripe account.</p>
          <button onClick={handleConnect} className={styles.button}>
            Connect with Stripe
          </button>
        </div>
      </div>
    );
  }

  return (
      <div className={styles.container}>
        <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Payment Settings</h3>
          <div className={`${styles.badge} ${styles[status.status]}`}>
            {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
          </div>
        </div>

        <div className={styles.accountInfo}>
          <div className={styles.infoRow}>
            <strong>Account ID:</strong>
            <span>{status.accountId}</span>
          </div>
          <div className={styles.infoRow}>
            <strong>Details Submitted:</strong>
            <span className={status.detailsSubmitted ? styles.enabled : ''}>
              {status.detailsSubmitted ? 'Yes' : 'No'}
            </span>
          </div>
          <div className={styles.infoRow}>
            <strong>Charges Enabled:</strong>
            <span className={status.chargesEnabled ? styles.enabled : ''}>
              {status.chargesEnabled ? 'Yes' : 'No'}
            </span>
          </div>
          <div className={styles.infoRow}>
            <strong>Payouts Enabled:</strong>
            <span className={status.payoutsEnabled ? styles.enabled : ''}>
              {status.payoutsEnabled ? 'Yes' : 'No'}
            </span>
          </div>
          {status.commissionRate && (
              <div className={styles.infoRow}>
              <strong>Commission Rate:</strong>
              <span>{status.commissionRate}%</span>
            </div>
          )}
        </div>

        {status.status !== 'active' && (
          <div className={styles.stripeActions}>
            <button onClick={handleConnect} className={styles.button}>
              Complete Stripe Setup
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSettings; 